/**
 * EditResultCard — renders a single edit operation result in the chat.
 *
 * Three card variants:
 *   - DiffCard (completed): shows before/after thumbnails + version badge
 *   - ProgressCard (processing): animated progress bar
 *   - StatusCard (pending/failed): status with optional error
 */

import type { EditResult } from './useChat';

interface EditResultCardProps {
  result: EditResult;
}

const statusConfig = {
  pending: {
    label: 'Queued',
    color: 'text-slate-500',
    bg: 'bg-slate-50',
    dot: 'bg-slate-300',
    border: 'border-slate-200',
  },
  processing: {
    label: 'Generating...',
    color: 'text-brand-teal-dark',
    bg: 'bg-teal-50',
    dot: 'bg-brand-teal animate-pulse',
    border: 'border-teal-200',
  },
  completed: {
    label: 'Applied',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    dot: 'bg-emerald-500',
    border: 'border-emerald-200',
  },
  failed: {
    label: 'Failed',
    color: 'text-danger',
    bg: 'bg-red-50',
    dot: 'bg-danger',
    border: 'border-red-200',
  },
};

export function EditResultCard({ result }: EditResultCardProps) {
  const cfg = statusConfig[result.status];

  return (
    <div className={`rounded-lg border ${cfg.border} ${cfg.bg} p-3 space-y-2 animate-fade-in`}>
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
          <span className={`text-[10px] font-mono uppercase tracking-wider ${cfg.color}`}>
            {cfg.label}
          </span>
          {result.version != null && result.status === 'completed' && (
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-teal-100 text-brand-teal-dark border border-teal-200">
              v{result.version}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {result.persona && (
            <span className="text-[9px] font-mono text-slate-400">{result.persona}</span>
          )}
          {result.headline && (
            <span className="text-[9px] text-slate-400 truncate max-w-[100px]">
              {result.headline}
            </span>
          )}
        </div>
      </div>

      {/* Instruction */}
      <p className="text-xs text-slate-600 leading-relaxed">{result.instruction}</p>

      {/* Diff Card: before/after thumbnails */}
      {result.status === 'completed' && result.after_url && (
        <div className="flex items-center gap-2 pt-1">
          {result.before_url && (
            <>
              <div className="relative w-14 h-14 rounded-md overflow-hidden border border-slate-200 bg-slate-100 shrink-0">
                <img src={result.before_url} alt="Before" className="w-full h-full object-cover opacity-60" />
                <div className="absolute bottom-0 inset-x-0 bg-white/80 text-[7px] font-mono text-center text-slate-400 py-px">
                  BEFORE
                </div>
              </div>
              <svg className="w-3 h-3 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </>
          )}
          <div className="relative w-14 h-14 rounded-md overflow-hidden border border-emerald-300 bg-slate-100 shrink-0 shadow-sm">
            <img src={result.after_url} alt="After" className="w-full h-full object-cover" />
            <div className="absolute bottom-0 inset-x-0 bg-white/80 text-[7px] font-mono text-center text-emerald-600 py-px">
              AFTER
            </div>
          </div>
        </div>
      )}

      {/* Processing animation */}
      {result.status === 'processing' && (
        <div className="space-y-1 pt-1">
          <div className="h-1 bg-teal-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-brand-teal/50 to-brand-teal rounded-full animate-pulse" style={{ width: '65%' }} />
          </div>
          <p className="text-[9px] font-mono text-slate-400">Generating creative...</p>
        </div>
      )}

      {/* Error detail */}
      {result.error && (
        <div className="bg-red-50 border border-red-200 rounded-md px-2 py-1.5">
          <p className="text-[10px] text-danger font-mono leading-relaxed">{result.error}</p>
        </div>
      )}
    </div>
  );
}

/**
 * EditSummaryCard — summary card shown after all edits complete.
 */
export function EditSummaryCard({
  completed,
  failed,
  quality,
}: {
  completed: number;
  failed: number;
  quality?: number | null;
}) {
  const total = completed + failed;
  const successRate = total > 0 ? completed / total : 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 animate-fade-in shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${successRate >= 0.8 ? 'bg-emerald-500' : successRate >= 0.5 ? 'bg-yellow-500' : 'bg-danger'}`} />
          <span className="text-xs text-slate-700 font-medium">
            {completed === total
              ? `All ${completed} edit${completed !== 1 ? 's' : ''} applied`
              : `${completed}/${total} edits applied`}
          </span>
        </div>
        {quality != null && (
          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
            Quality: {(quality * 100).toFixed(0)}%
          </span>
        )}
      </div>
    </div>
  );
}
